import { useMemo, useRef, useCallback } from 'react';
import type { ImageStyleProps } from './useComputedStyle';
import type { CanvasData } from '../utils/canvas-utils';
import type { TextParticlesProps } from '../../TextParticles.types';

type UseImageLoaderParams = {
  canvasData: CanvasData;
  imageProps: ImageStyleProps | undefined;
  text: string;
  textDirection: TextParticlesProps['textDirection'];
};

export const useImageLoader = ({
  canvasData,
  imageProps,
  text,
  textDirection,
}: UseImageLoaderParams) => {
  const previousCacheRef = useRef<string>('');
  const reusableImageCanvasRef = useRef<{
    canvas: OffscreenCanvas;
    context: OffscreenCanvasRenderingContext2D;
  } | null>(null);

  const getImageCanvas = useCallback(() => {
    if (!reusableImageCanvasRef.current) {
      const canvas = new OffscreenCanvas(1, 1);
      const context = canvas.getContext('2d', {
        willReadFrequently: true,
      }) as OffscreenCanvasRenderingContext2D;

      if (!context) {
        throw new Error('Failed to get canvas context');
      }

      reusableImageCanvasRef.current = { canvas, context };
    }

    return reusableImageCanvasRef.current;
  }, []);

  const images = useMemo(() => {
    if (
      !canvasData ||
      canvasData.displayWidth === 0 ||
      canvasData.displayHeight === 0 ||
      !imageProps
    ) {
      return null;
    }

    const currentCache = getImageCache(
      canvasData,
      imageProps,
      text,
      textDirection,
    );
    const previousCache = previousCacheRef.current;

    if (previousCache === currentCache) {
      return null;
    }

    const { canvas, context } = getImageCanvas();

    const imageBitmap = generateTextImage({
      canvas,
      context,
      canvasData,
      imageProps,
      text,
      textDirection,
    });

    const highResImageBitmap = generateTextImage({
      canvas,
      context,
      canvasData,
      imageProps,
      text,
      textDirection,
      resolutionMultiplier: 2,
    });

    previousCacheRef.current = currentCache;
    return { imageBitmap, highResImageBitmap };
  }, [imageProps, canvasData, text, textDirection, getImageCanvas]);

  return images;
};

// Not exporting this on purpose, use CSS text-transform where available
const transformText = (text: string, textTransform: string) => {
  if (textTransform === 'uppercase') {
    return text.toUpperCase();
  }
  if (textTransform === 'lowercase') {
    return text.toLowerCase();
  }
  if (textTransform === 'capitalize') {
    // Split by common separators and capitalize the first letter of each part
    return text.replace(/[^\s\-_.,;:!?()[\]{}/\\|+=&@#$%^*~`'"]+/g, word => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
  }
  return text;
};

const getImageCache = (
  canvasData: CanvasData,
  imageProps: ImageStyleProps | undefined,
  text: string,
  textDirection: TextParticlesProps['textDirection'],
) => {
  if (!canvasData || !imageProps) {
    return '';
  }

  const cacheData = {
    canvasData: {
      offscreenWidth: canvasData.offscreenWidth,
      offscreenHeight: canvasData.offscreenHeight,
      devicePixelRatio: canvasData.devicePixelRatio,
      displayWidth: canvasData.displayWidth,
      displayHeight: canvasData.displayHeight,
    },
    imageProps: {
      color: imageProps.color,
      font: imageProps.font,
      letterSpacing: imageProps.letterSpacing,
      textTransform: imageProps.textTransform,
      fontReady: imageProps.fontReady,
    },
    text,
    textDirection,
  };

  return JSON.stringify(cacheData);
};

const generateTextImage = ({
  canvas,
  context,
  canvasData,
  imageProps,
  text,
  textDirection,
  resolutionMultiplier = 1,
}: {
  canvas: OffscreenCanvas;
  context: OffscreenCanvasRenderingContext2D;
  canvasData: NonNullable<CanvasData>;
  imageProps: ImageStyleProps;
  text: string;
  textDirection: TextParticlesProps['textDirection'];
  resolutionMultiplier?: number;
}): ImageBitmap => {
  const resultWidth = canvasData.offscreenWidth * resolutionMultiplier;
  const resultHeight = canvasData.offscreenHeight * resolutionMultiplier;

  if (canvas.width !== resultWidth || canvas.height !== resultHeight) {
    canvas.width = resultWidth;
    canvas.height = resultHeight;
  } else {
    context.clearRect(0, 0, canvas.width, canvas.height);
  }

  const scale = canvasData.devicePixelRatio * resolutionMultiplier;
  context.scale(scale, scale);
  context.textBaseline = 'middle';
  context.textAlign = 'center';
  context.fillStyle = imageProps.color;
  context.font = imageProps.font;
  context.direction = textDirection;
  // letterSpacing is not part of the official CanvasRenderingContext2D interface
  (context as any).letterSpacing = imageProps.letterSpacing;
  // Canvas doesn't support text-transform, so we need to transform the text here
  const transformedText = transformText(text, imageProps.textTransform);
  context.fillText(
    transformedText,
    canvasData.displayWidth / 2,
    canvasData.displayHeight / 2,
  );

  return canvas.transferToImageBitmap();
};
